import React, { FC, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { ArrowNarrowDirection } from '../../../../assets/icons/arrow-narrow-icon';
import { ArrowNarrowIcon, CheckIcon, LockIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { FormRadio, FormRadioGroup } from '../../../../components/radio-group/index';
import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useDebounce, useFormat, useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import { DilutionConditionTitle } from '../../../../types/pool-plans.types';
import { ChildrenFormProps } from '../pool-form.types';
import { FormSchema, stepOne as stepOneFormSchema } from '../pool-form.validation';
import { ShareClassCombobox } from '../share-class-combobox';

export type StepOneProps = ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  handleCloseModal,
  nextFormStep,
  control,
  setFormData,
  companyId,
  lockMode,
  clearErrors,
  setError,
  filedState,
  onAdjustPoolSizeClick,
}) => {
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const { success } = stepOneFormSchema.safeParse(stepOne);
  const { messagesLocale } = useLocale();
  const { mutate } = useAppMutation([QueryKey.POOL_NAME_CHECK, { name: stepOne?.name || '' }], {
    mutationFn: async (name: string) =>
      await ApiService.get<{ isInUse: boolean }>({
        endpoint: BackendRoute.POOLS,
        routePath: ['check-is-name-in-use', name],
      }),
    onSuccess: ({ isInUse }) => {
      setError('stepOne.name', {
        message: `${messagesLocale['NAME_SHOULD_BE_UNIQUE']}`,
        type: 'validate',
        types: { required: true },
      });
      if (!isInUse) return clearErrors('stepOne.name');
    },
    defaultErrorHandling: false,
  });

  const isValid = success && !filedState('stepOne').invalid;

  const { debounceCallback } = useDebounce<string>({
    callback: (name) => name && mutate(name),
    debounce: 300,
  });

  stepOne;

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <FormInput
          control={control}
          name="stepOne.name"
          onChange={debounceCallback}
          placeholder={<AppFormattedMessage id="POOL_NAME" />}
        />
        {lockMode ? (
          <div className="flex w-full flex-col gap-3 rounded-md bg-brand-25 px-[2px] pb-3 pt-[2px]">
            <div className="w-full overflow-hidden rounded-md bg-white shadow-sm">
              <FormInput
                className="text-gray-500"
                control={control}
                disabled
                icon={<LockIcon iconColor="#98A2B3" strokeWidth={0.9} />}
                inputClassName="rounded-md bg-white"
                name="stepOne.dillutedSharesCount"
                numberOnly
                placeholder={<AppFormattedMessage id="FULLY_DILUTED_SHARES" />}
                shouldFormatNumber
              />
            </div>
            {onAdjustPoolSizeClick && (
              // The underline is always drawn, transparent at rest, so hovering fades it in.
              <button
                className="group flex w-fit items-center gap-2 px-[14px] text-sm font-[450] text-brand-700 underline decoration-transparent transition-colors duration-200 hover:decoration-brand-700 motion-reduce:transition-none"
                data-permission-required
                onClick={onAdjustPoolSizeClick}
                type="button"
              >
                <AppFormattedMessage id="ADJUST_POOL_SIZE" />
                <ArrowNarrowIcon
                  className="size-5 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
                  direction={ArrowNarrowDirection.RIGHT}
                  iconColor="#2565C8"
                  strokeWidth={1.56}
                />
              </button>
            )}
          </div>
        ) : (
          <FormInput
            control={control}
            name="stepOne.dillutedSharesCount"
            numberOnly
            placeholder={<AppFormattedMessage id="FULLY_DILUTED_SHARES" />}
            shouldFormatNumber
          />
        )}
        <ShareClassCombobox companyId={companyId} control={control} />
        <FormDatePicker
          inputValue={stepOne?.date ? format(stepOne?.date, 'dd/MM/yyyy') : ''}
          inputWrapperClassName="w-full"
          isDefaultOpenCalendar={isOpenCalendar}
          onSelect={(date) => {
            setFormData('stepOne.date', date);
            setOpenCalendar(false);
          }}
          placeholder={<AppFormattedMessage id="CREATION_DATE" />}
          value={stepOne?.date}
        />

        <div className="flex w-full flex-col gap-3 rounded bg-gray-50 px-3 py-2">
          <span className="text-base font-[550] text-gray-700">
            <AppFormattedMessage id="DILUTION_CONDITION" />
          </span>

          <FormRadioGroup
            className={'flex shrink-0 flex-col gap-3 divide-x-0 overflow-x-auto'}
            control={control}
            name="stepOne.dilutionCondition"
          >
            {Object.entries(DilutionConditionTitle).map(([dilutionKey, dilutionTitle]) => (
              <div className="flex items-center gap-3" key={dilutionKey}>
                <FormRadio
                  className={
                    'flex size-4 items-center justify-center overflow-hidden rounded-full border border-gray-300 data-[checked]:border-none data-[checked]:bg-brand-500'
                  }
                  control={control}
                  name="stepOne.dilutionCondition"
                  styleType="NONE"
                  value={dilutionKey}
                >
                  <CheckIcon
                    className="hidden size-[10px] group-data-[checked]:block"
                    iconColor={'#ffffff'}
                  />
                </FormRadio>
                <span className="text-nowrap text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id={dilutionTitle} />
                </span>
              </div>
            ))}
          </FormRadioGroup>
        </div>

        <div className="flex flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
          <DropDown
            answer={messagesLocale['AMOUNT_OF_FULLY_DILUTED_SHARES_EXPLANATION']}
            question={<AppFormattedMessage id="AMOUNT_FULLY_DILUTED_SHARES_HELP" />}
          />
          <DropDown
            answer={[
              messagesLocale['AMOUNT_OF_FULLY_DILUTED_SHARES_EXPLANATION_1'],
              messagesLocale['AMOUNT_OF_FULLY_DILUTED_SHARES_EXPLANATION_2'],
              messagesLocale['AMOUNT_OF_FULLY_DILUTED_SHARES_EXPLANATION_3'],
              messagesLocale['AMOUNT_OF_FULLY_DILUTED_SHARES_EXPLANATION_4'],
            ]}
            question={<AppFormattedMessage id="DILUTION_CONDITIONS_HELP" />}
          />
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
